import React from "react"
import { Pencil, UsersRound, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Skeleton } from "src/components/ui/skeleton"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?"
}

function SkeletonRow() {
  return (
    <TableRow>
      {[40, 200, 200, 140, 120, 50].map((w, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 rounded" style={{ width: w }} />
        </TableCell>
      ))}
    </TableRow>
  )
}

function EmptyState({ hasFilters }) {
  return (
    <TableRow>
      <TableCell colSpan={6}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UsersRound className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">No members found</p>
          <p className="mt-1 text-xs text-slate-400">
            {hasFilters ? "Try adjusting your search" : "Invite teammates to get started"}
          </p>
        </div>
      </TableCell>
    </TableRow>
  )
}

export function MembersTable({ members = [], loading, hasFilters, onEdit }) {
  const [sortField, setSortField] = React.useState(null)
  const [sortDirection, setSortDirection] = React.useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedMembers = React.useMemo(() => {
    if (!sortField || !sortDirection) return members
    return [...members].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.user?.name || ""
        bVal = b.user?.name || ""
      } else if (sortField === "email") {
        aVal = a.user?.email || ""
        bVal = b.user?.email || ""
      } else if (sortField === "phone") {
        aVal = a.user?.phone || ""
        bVal = b.user?.phone || ""
      } else if (sortField === "role") {
        aVal = a.role?.name || ""
        bVal = b.role?.name || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [members, sortField, sortDirection])

  const renderSortableHeader = (label, field) => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className="inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black"
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="min-w-[220px] text-black">{renderSortableHeader("Name", "name")}</TableHead>
            <TableHead className="min-w-[200px] text-black">{renderSortableHeader("Email", "email")}</TableHead>
            <TableHead className="w-[160px] text-black">{renderSortableHeader("Phone", "phone")}</TableHead>
            <TableHead className="w-[150px] text-black">{renderSortableHeader("Role", "role")}</TableHead>
            <TableHead className="w-[60px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
          ) : members.length === 0 ? (
            <EmptyState hasFilters={hasFilters} />
          ) : (
            sortedMembers.map((m, idx) => (
              <TableRow key={m.id}>
                <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecebff] text-[11px] font-semibold text-[#4f39f6]">
                      {initials(m.user?.name)}
                    </span>
                    <span className="text-[13px] font-medium text-slate-900">{m.user?.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[13px] text-slate-600">{m.user?.email}</TableCell>
                <TableCell className="text-[13px] text-slate-600">
                  {m.user?.phone || <span className="text-slate-300">No phone number</span>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-medium text-slate-700">
                    {m.role?.name}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(m)} title="Edit member">
                      <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
